import PageComponent from "../../components/page/PageComponent.vue";
import SectionSheet from "../../components/section_sheet/SectionSheet.vue";
import PageControl from "../../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "CompanyBankAccountReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: '.sheet',
      required: false
    },
    company_account_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        company_information: false,
        verification_results: false,
        transaction_details: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    has_selected_tables() {
      return Object.values(this.export_options).some(option => option);
    },
    safe_company_account_data() {
      return this.company_account_data || {};
    },
    
    // Basic Company Information
    company_name() {
      return this.safe_company_account_data.surname || 'N/A';
    },
    
    registration_number() {
      return this.safe_company_account_data.id_number || 'N/A';
    },
    
    account_number() {
      return this.safe_company_account_data.account_number || 'N/A';
    },
    
    branch_number() {
      return this.safe_company_account_data.branch_number || 'N/A';
    },
    
    account_type() {
      return this.safe_company_account_data.account_type || 'N/A';
    },
    
    account_type_description() {
      const accountTypes = {
        '01': 'Current Account',
        '02': 'Savings Account',
        '03': 'Credit Account',
        '04': 'Transmission Account',
        '05': 'Bond Account',
        '06': 'Subscription Share Account'
      };

      return accountTypes[this.account_type] || `Account Type ${this.account_type}`;
    },
    
    // Transaction Details
    client_user_reference() {
      return this.safe_company_account_data.client_user_reference || 'N/A';
    },
    
    sub_billing_id() {
      return this.safe_company_account_data.sub_billing_id || 'N/A';
    },
    
    record_indicator() {
      return this.safe_company_account_data.record_indicator || 'N/A';
    },
    
    sequence_number() {
      return this.safe_company_account_data.sequence_number || 'Not Available';
    },
    
    current_date() {
      return new Date().toISOString();
    },
    
    formatted_current_date() {
      return this.formatDate(this.current_date);
    },
    
    // Verification Results
    account_found() {
      return this.safe_company_account_data.account_found || 'N/A';
    },
    
    id_number_match() {
      return this.safe_company_account_data.id_number_match || 'N/A';
    },
    
    initials_match() {
      return this.safe_company_account_data.initials_match || 'Not Available';
    },
    
    surname_match() {
      return this.safe_company_account_data.surname_match || 'N/A';
    },
    
    account_open() {
      return this.safe_company_account_data.account_open || 'N/A';
    },
    
    account_open_for_atleast_three_months() {
      return this.safe_company_account_data.account_open_for_atleast_three_months || 'N/A';
    },
    
    account_accepts_debits() {
      return this.safe_company_account_data.account_accepts_debits || 'N/A';
    },
    
    account_accepts_credits() {
      // This field is not in the provided data, so we'll mark it as not available
      return 'Not Available';
    },
    
    // Additional Account Details
    account_type_return() {
      return this.safe_company_account_data.account_type_return || 'N/A';
    },
    
    account_dormant() {
      return this.safe_company_account_data.account_dormant || 'Not Available';
    },
    
    tax_reference_match() {
      return this.safe_company_account_data.tax_reference_match || 'Not Available';
    },
    
    email_match() {
      return this.safe_company_account_data.email_match || 'Not Available';
    },
    
    phone_match() {
      return this.safe_company_account_data.phone_match || 'Not Available';
    },
    
    id_type() {
      return this.safe_company_account_data.id_type || 'Not Available';
    },
    
    account_issuer() {
      return this.safe_company_account_data.account_issuer || 'Not Available';
    },
    
    error_condition_number() {
      return this.safe_company_account_data.error_condition_number || 'Not Available';
    },
    
    tax_reference_number() {
      return this.safe_company_account_data.tax_reference_number || 'Not Available';
    },
    
    // Contact Information
    email_address() {
      return this.safe_company_account_data.email_address || null;
    },
    
    // Report metadata
    report_reference() {
      return this.client_user_reference;
    },
    
    // Overall verification status
    overall_verification_status() {
      const criticalChecks = [
        this.account_found,
        this.id_number_match,
        this.surname_match,
        this.account_open
      ];
      
      const allPassed = criticalChecks.every(check => check === 'Yes');
      return allPassed ? 'Verified' : 'Failed';
    },
    
    // Badge classes and icons
    verification_status_badge_class() {
      return this.overall_verification_status === 'Verified' ? 'status-good' : 'status-bad';
    },
    
    verification_status_icon() {
      return this.overall_verification_status === 'Verified' ? 'fas fa-check-circle' : 'fas fa-times-circle';
    },
    
    modal_instance() {
      var modal = new window.bootstrap.Modal(document.getElementById(`export-modal-${this.report_reference}`))
      return modal;
    }
  },
  
  methods: {
    close_modal() {
      this.modal_instance.hide();
    },
    
    // Utility methods
    formatDate(date) {
      return format_date(date);
    },
    
    get_verification_badge_class(result) {
      if (result === 'Yes') return 'bg-success';
      if (result === 'No') return 'bg-danger';
      return 'bg-warning'; // For 'Not Available' or other values
    },
    
    get_verification_icon(result) {
      if (result === 'Yes') return 'fas fa-check';
      if (result === 'No') return 'fas fa-times';
      return 'fas fa-question'; // For 'Not Available' or other values
    },
    
    get_verification_status_text(result) {
      if (result === 'Yes') return 'Pass';
      if (result === 'No') return 'Fail';
      return 'N/A'; // For 'Not Available' or other values
    },
    
    // Export functionality
    show_export_modal() {
      this.export_options.company_information = true;
      this.export_options.verification_results = true;
      this.export_options.transaction_details = true;
      this.export_options.raw_json = false;
      this.modal_instance.show();
    },
    
    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js');
        this.libraries_loaded.papa_parse = true;
      }
      
      if (!this.libraries_loaded.sheetjs) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.0/xlsx.full.min.js');
        this.libraries_loaded.sheetjs = true;
      }
    },
    
    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    async export_data() {
      await this.load_export_libraries();
      
      if (this.export_format === 'csv') {
        await this.export_csv();
      } else if (this.export_format === 'excel') {
        await this.export_excel();
      } else if (this.export_format === 'json') {
        await this.export_json();
      }
      
      // Close modal
      this.close_modal();
    },
    
    // Prepare company information data
    prepare_company_information_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Company Name": this.company_name,
          "Registration Number": this.registration_number,
          "Account Number": this.account_number,
          "Branch Code": this.branch_number,
          "Account Type": this.account_type_description,
          "Account Type Code": this.account_type,
          "Verification Status": this.overall_verification_status,
          "Sub Billing ID": this.sub_billing_id,
          "Client Reference": this.client_user_reference,
          "Record Indicator": this.record_indicator,
          "Generated Date": this.formatted_current_date,
          "Email Address": this.email_address || 'Not Available'
        }
      ];
    },
    
    // Prepare verification results data
    prepare_verification_results_data() {
      return [
        {
          "Verification Check": "Account Found",
          "Description": "Verify if account exists in banking system",
          "Result": this.account_found,
          "Status": this.get_verification_status_text(this.account_found)
        },
        {
          "Verification Check": "Registration Number Match",
          "Description": "Verify if registration number matches account holder",
          "Result": this.id_number_match,
          "Status": this.get_verification_status_text(this.id_number_match)
        },
        {
          "Verification Check": "Company Name Match",
          "Description": "Verify if company name matches account holder",
          "Result": this.surname_match,
          "Status": this.get_verification_status_text(this.surname_match)
        },
        {
          "Verification Check": "Account Open",
          "Description": "Verify if account is currently active",
          "Result": this.account_open,
          "Status": this.get_verification_status_text(this.account_open)
        },
        {
          "Verification Check": "Account Age (3+ Months)",
          "Description": "Verify if account has been open for at least 3 months",
          "Result": this.account_open_for_atleast_three_months,
          "Status": this.get_verification_status_text(this.account_open_for_atleast_three_months)
        },
        {
          "Verification Check": "Accepts Debits",
          "Description": "Verify if account can receive debit transactions",
          "Result": this.account_accepts_debits,
          "Status": this.get_verification_status_text(this.account_accepts_debits)
        },
        {
          "Verification Check": "Email Match",
          "Description": "Verify if email address matches account records",
          "Result": this.email_match,
          "Status": this.get_verification_status_text(this.email_match)
        },
        {
          "Verification Check": "Account Type Return",
          "Description": "Verify if account type information was returned",
          "Result": this.account_type_return,
          "Status": this.get_verification_status_text(this.account_type_return)
        },
        {
          "Verification Check": "Tax Reference Match",
          "Description": "Tax reference number verification status",
          "Result": this.tax_reference_match,
          "Status": this.get_verification_status_text(this.tax_reference_match)
        },
        {
          "Verification Check": "Account Dormant",
          "Description": "Status of account dormancy",
          "Result": this.account_dormant,
          "Status": this.get_verification_status_text(this.account_dormant)
        }
      ];
    },
    
    // Prepare transaction details data
    prepare_transaction_details_data() {
      return [
        {
          "Transaction Reference": this.client_user_reference,
          "Generated Date": this.formatted_current_date,
          "Sub Billing ID": this.sub_billing_id,
          "Record Indicator": this.record_indicator,
          "Sequence Number": this.sequence_number,
          "Company Name Requested": this.company_name,
          "Registration Number Requested": this.registration_number,
          "Account Number Requested": this.account_number,
          "Branch Code Requested": this.branch_number,
          "Account Type Requested": this.account_type_description,
          "Account Type Code": this.account_type,
          "Email Address": this.email_address || 'Not Available',
          "ID Type": this.id_type,
          "Tax Reference Number": this.tax_reference_number,
          "Account Issuer": this.account_issuer,
          "Error Condition": this.error_condition_number
        }
      ];
    },
    
    // Prepare raw JSON data
    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          client_user_reference: this.client_user_reference,
          generated_date: this.formatted_current_date,
          export_date: new Date().toISOString(),
          report_type: 'Company Account Verification Report'
        },
        raw_company_verification_data: this.safe_company_account_data
      };
    },
    
    // Validation method to test export data completeness
    validate_export_data() {
      const validation_report = {
        company_information: {
          available: !!this.company_name && !!this.registration_number && !!this.account_number,
          data_count: this.prepare_company_information_data().length
        },
        verification_results: {
          available: !!this.account_found,
          data_count: this.prepare_verification_results_data().length
        },
        transaction_details: {
          available: !!this.client_user_reference,
          data_count: this.prepare_transaction_details_data().length
        }
      };
      
      return validation_report;
    },
    
    // Export methods
    async export_csv() {
      const Papa = window.Papa;
      const current_date = new Date().toISOString().split('T')[0];
      
      // Map section keys to their data preparation methods and file suffixes
      const sectionMap = [
        {
          key: 'company_information',
          label: 'Company_Information',
          prepare: this.prepare_company_information_data
        },
        {
          key: 'verification_results',
          label: 'Verification_Results',
          prepare: this.prepare_verification_results_data
        },
        {
          key: 'transaction_details',
          label: 'Transaction_Details',
          prepare: this.prepare_transaction_details_data
        }
      ];

      // For each selected section, generate and download a CSV
      for (const section of sectionMap) {
        if (this.export_options[section.key]) {
          const data = section.prepare.call(this);
          if (data && data.length > 0) {
            const csv = Papa.unparse(data);
            const filename = `Company_Account_Verification_Report_${section.label}_${current_date}.csv`;
            this.download_file(csv, filename, 'text/csv');
          }
        }
      }
    },
    
    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = new Date().toISOString().split('T')[0];
      const workbook = XLSX.utils.book_new();
      
      if (this.export_options.company_information) {
        const company_data = this.prepare_company_information_data();
        const worksheet = XLSX.utils.json_to_sheet(company_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Company Information');
      }
      
      if (this.export_options.verification_results) {
        const verification_data = this.prepare_verification_results_data();
        const worksheet = XLSX.utils.json_to_sheet(verification_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Verification Results');
      }
      
      if (this.export_options.transaction_details) {
        const transaction_data = this.prepare_transaction_details_data();
        const worksheet = XLSX.utils.json_to_sheet(transaction_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transaction Details');
      }
      
      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, create a simple table with the JSON as text
        const json_table = [
          {
            'JSON Data': JSON.stringify(json_data, null, 2)
          }
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Raw JSON Data');
      }
      
      const filename = `Company_Account_Verification_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },
    
    async export_json() {
      const current_date = new Date().toISOString().split('T')[0];
      const export_data = {};
      
      if (this.export_options.company_information) {
        export_data.company_information = this.prepare_company_information_data()[0];
      }
      
      if (this.export_options.verification_results) {
        export_data.verification_results = this.prepare_verification_results_data();
      }
      
      if (this.export_options.transaction_details) {
        export_data.transaction_details = this.prepare_transaction_details_data()[0];
      }
      
      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }
      
      const json = JSON.stringify(export_data, null, 2);
      const filename = `Company_Account_Verification_Report_${current_date}.json`;
      this.download_file(json, filename, 'application/json');
    },
    
    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};
